import { useState } from "react";

import { useMsal } from "@azure/msal-react";
import axios from "axios";

import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";
import { useTeamsFiles } from "@/hooks/useTeamsFiles";
import { TeamsFile } from "@/util/interfaces/TeamsFile";

export const useExploreFolder = () => {
  const { instance, accounts } = useMsal();
  const { msalConfig } = useTeamsAndChannels();
  const { addTeamsFiles, updateTeamsFile, teamsFiles } = useTeamsFiles();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchFolderFiles = async (
    folderId: string,
    selectedChannel: { drive_id: string; item_id: string; channelName: string; teamName: string }
  ): Promise<TeamsFile[]> => {
    if (!accounts[0]) return [];

    try {
      const response = await instance.acquireTokenSilent({
        scopes: msalConfig.backend_uri?.scopes || ["User.Read"],
        account: accounts[0],
      });

      const url = `${process.env.REACT_APP_SERVER_HOST}/teams-sync/drives/${selectedChannel.drive_id}/items/${folderId}/files`;
      const { data } = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${response.accessToken}`,
        },
      });

      return data
        .filter((document: TeamsFile) => {
          if (document.folder?.childCount === 0) {
            return false; // Exclude empty folders
          }
          return true; // Include files and non-empty folders
        })
        .map((document: TeamsFile) => ({
          ...document,
          channel: selectedChannel,
          explored: !document.folder,
          metadata: "",
          category: null,
          selected: false,
        })) as TeamsFile[];
    } catch (error) {
      console.error("Error fetching folder files:", error);
      return [];
    }
  };

  const processExploredFolder = async (
    folder: TeamsFile,
    selectedChannel: { drive_id: string; item_id: string; channelName: string; teamName: string }
  ): Promise<void> => {
    const children = teamsFiles.filter((file: TeamsFile) => file.parentReference.id === folder.id);

    for (const child of children) {
      if (child.folder) {
        await exploreFolder(child, selectedChannel);
      }
    }
  };

  const processUnexploredFolder = async (
    folder: TeamsFile,
    selectedChannel: { drive_id: string; item_id: string; channelName: string; teamName: string }
  ): Promise<void> => {
    const folderFiles = await fetchFolderFiles(folder.id, selectedChannel);

    if (folderFiles.length > 0) {
      addTeamsFiles(folderFiles);
      updateTeamsFile(folder.id, { explored: true });

      // Recursively explore subfolders
      for (const file of folderFiles) {
        if (file.folder) {
          await exploreFolder(file, selectedChannel);
        }
      }
    }
  };

  const exploreFolder = async (
    folder: TeamsFile,
    selectedChannel: { drive_id: string; item_id: string; channelName: string; teamName: string }
  ): Promise<void> => {
    if (!folder.folder || !selectedChannel) return;

    setIsLoading(true);
    try {
      if (folder.explored) {
        await processExploredFolder(folder, selectedChannel);
      } else {
        await processUnexploredFolder(folder, selectedChannel);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const exploreFolderInBackground = (
    folder: TeamsFile,
    selectedChannel: { drive_id: string; item_id: string; channelName: string; teamName: string }
  ): void => {
    // Execute in background without blocking UI
    exploreFolder(folder, selectedChannel).catch((error) => {
      console.error("Background folder exploration failed:", error);
    });
  };

  return {
    exploreFolder,
    exploreFolderInBackground,
    isLoading,
  };
};
